// A V2.9 Input row's collection controls: Type, that type's settings, and Optional.
import { Checkbox } from '@neo/primitives/checkbox'
import { Select } from '@neo/primitives/select'
import { Tooltip } from '@neo/primitives/tooltip'
import { Typography } from '@neo/primitives/typography'
import { useState } from 'react'
import { useFlowStore } from '../../store'
import { typeLabel } from './agent-tasks-config'
import {
  applyTick,
  applyType,
  OPTIONAL_TICK,
  reasonNote,
  settingFieldsFor,
  TYPE_OPTIONS,
  ticksOf,
} from './collection-configuration-config'
import type { CollectionConfig } from './collection-configuration-types'
import { CollectionTypeSettings } from './collection-more-settings'

export const CollectionConfiguration = ({
  value: rawValue,
  onChange,
  form,
  baseName,
}: {
  // `unknown` so the component stays assignable to the form's generic CompProps
  value: unknown
  onChange: (next: CollectionConfig) => void
  // biome-ignore lint/suspicious/noExplicitAny: the sidebar form is generic over the node's config
  form: any
  baseName?: string
}) => {
  const value = rawValue as CollectionConfig | undefined
  const disabled = !useFlowStore((state) => state.isEditing)
  const variableName = (form.getFieldValue(`${baseName}.variable_name`) ?? '') as string
  // a detection that could not configure THIS Input still says why, on this row
  const reason = reasonNote(useFlowStore((state) => state.collectionDetection.errors[variableName]))
  const [settingsOpen, setSettingsOpen] = useState(false)
  const ticks = ticksOf(value)
  // a type with no settings of its own shows no disclosure at all, as V2.5 shows none
  const hasSettings = settingFieldsFor(value?.type).length > 0

  return (
    <div className="mt-1 flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span className="text-s-400 text-secondary">Type</span>
        <div className="w-[190px]" data-testid="select-collect-type">
          <Select
            name={`${baseName}-collect-type`}
            options={TYPE_OPTIONS}
            value={value?.type ?? ''}
            placeholder="Not detected yet"
            searchable={false}
            virtualized={false}
            size="sm"
            disabled={disabled}
            onChange={(next) => onChange(applyType(value, next as string))}
          />
        </div>
      </div>

      {reason && (
        <Typography
          variant="XS/400"
          className="text-status-danger"
          data-testid="text-collection-note"
        >
          {reason}
        </Typography>
      )}

      {hasSettings && (
        <div className="border-t border-dashed border-st2 pt-2.5">
          <button
            type="button"
            className="text-xs-600 text-secondary hover:text-foreground"
            data-testid="button-type-settings"
            onClick={() => setSettingsOpen((open) => !open)}
          >
            {settingsOpen ? '▾' : '▸'} {typeLabel(value?.type ?? '')} settings
          </button>
          {settingsOpen && (
            <div className="mt-2.5">
              <CollectionTypeSettings
                value={value}
                disabled={disabled}
                baseName={baseName}
                onChange={onChange}
              />
            </div>
          )}
        </div>
      )}

      {/* V2.5 closes its variable row the same way: Optional, on its own line, flush right. */}
      <div className="flex justify-end border-t border-st2 pt-3">
        <Tooltip content={OPTIONAL_TICK.tooltip}>
          <div>
            <Checkbox
              name={`${baseName}-optional`}
              data-testid="checkbox-collection-optional"
              label={OPTIONAL_TICK.label}
              disabled={disabled}
              value={ticks.optional}
              onChange={(checked: boolean) => onChange(applyTick(value, 'optional', checked))}
            />
          </div>
        </Tooltip>
      </div>
    </div>
  )
}
